import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Globe, Search, Plus, Settings2, Trash2, Rocket } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Input, Skeleton } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWaapCdnDomains,
  type WaapPublicWebsite,
  useDeleteWaapWebsite,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { WaapEmptyCard } from '@/pages/services/waap/WaapEmptyCard';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import styles from './WebsitesTab.module.css';

const muted = (v: React.ReactNode) => <span className={styles.muted}>{v}</span>;

/** Normalise a domain for matching the vendor list against the service registry. */
const normDomain = (d: string) => d.trim().toLowerCase().replace(/\.$/, '');

/**
 * WAAP Websites list — the account's website list over the PUBLIC Open-API
 * (GET /websites). The public DTO is slim: domain / alternate domains /
 * service id / origins / http2 / redirect — it has NO protection / protection-mode
 * / certificate / status metadata (those only exist on the private /cdn/domains
 * surface), so those columns are not shown here.
 *
 * Per-website actions (Configure / Remove Security) need the platform domain UUID,
 * which only exists for domains registered in our service; those are matched by
 * name against the service registry, and the action is disabled for vendor-only
 * domains not (yet) imported here.
 */
export function WebsitesTab() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { websites, selected, setSelectedDomainId } = useWaapWebsite();
  const { show } = useToast();
  const qc = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [pendingDelete, setPendingDelete] = useState<WaapPublicWebsite | null>(null);

  const anchorId = selected?.domainId;
  const { data: domains = [], isLoading } = useWaapCdnDomains(anchorId);
  const base = `/services/waap/${serviceId}`;

  // The vendor website list has no platform UUID; map by domain name to the
  // service registry so per-website actions can address the right domain.
  // Names are normalised (lower-cased, trailing dot stripped) so casing / FQDN
  // dot differences between the public list and the registry still match.
  const uuidByName = useMemo(() => {
    const m = new Map<string, string>();
    websites.forEach((w) => m.set(normDomain(w.domain), w.domainId));
    return m;
  }, [websites]);

  const domainUuid = (domain: string) => uuidByName.get(normDomain(domain));

  const rows = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    return q ? domains.filter((d) => d.domain.toLowerCase().includes(q)) : domains;
  }, [domains, searchInput]);

  // Delete the whole website (removes the vendor CDN-domain config entirely), keyed
  // by the vendor website id — available for every listed website, unlike the
  // config/reset actions which need the platform UUID.
  const deleteWebsite = useDeleteWaapWebsite(anchorId, {
    onSuccess: () => {
      // Deletion is asynchronous on the backend — the request is queued and the
      // vendor removes the website + its dependencies over the next few minutes.
      show({
        tone: 'success',
        title: 'Deletion requested',
        description: 'The website removal has been requested. It may take a few minutes for the system to remove all its dependencies.',
      });
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey: ['waap', 'cdn-domains'] });
      // The backend now also drops the WAAP registry binding for the deleted
      // website, so refresh the service's registry list (drives name→UUID + count).
      if (serviceId) qc.invalidateQueries({ queryKey: ['waap', 'service', serviceId, 'domains'] });
    },
    onError: (e) =>
      show({ tone: 'danger', title: 'Failed to delete website', description: errMessage(e) }),
  });

  // Per-website config/reset are keyed by the platform domain UUID, which only
  // exists for domains registered in this service. A vendor-only website (listed
  // by the Open-API but not imported here) has none — tell the user instead of
  // silently doing nothing.
  const notRegistered = (domain: string) =>
    show({
      tone: 'warning',
      title: 'Website not configurable yet',
      description: `"${domain}" isn't registered in this service, so its security settings can't be opened here. Add it to the service first.`,
    });

  const configure = (d: WaapPublicWebsite) => {
    const id = domainUuid(d.domain);
    if (!id) return notRegistered(d.domain);
    setSelectedDomainId(id);
    navigate(`${base}/websites/${id}`);
  };

  const columns = useMemo<ColumnDef<WaapPublicWebsite>[]>(
    () => [
      {
        accessorKey: 'domain',
        header: 'Domain',
        cell: ({ row }) => <span className={styles.name}>{row.original.domain}</span>,
      },
      {
        id: 'alternateDomainNames',
        header: 'Alternate Domain Names',
        cell: ({ row }) =>
          row.original.alternate_domain_names?.length
            ? row.original.alternate_domain_names.join(', ')
            : muted('—'),
      },
      {
        accessorKey: 'service_id',
        header: 'Service ID',
        cell: ({ row }) => muted(row.original.service_id),
      },
      {
        id: 'created',
        header: 'Create Time',
        cell: ({ row }) => muted(row.original.created || '—'),
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <div className={styles.actions}>
            <Button
              size="sm"
              variant="secondary"
              startIcon={<Settings2 size={14} />}
              onClick={() => configure(row.original)}
            >
              Configure
            </Button>
            <Button
              size="sm"
              variant="danger"
              startIcon={<Trash2 size={14} />}
              onClick={() => setPendingDelete(row.original)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, uuidByName],
  );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'Websites' }]} />}
        title={`Websites${domains.length ? ` (${domains.length})` : ''}`}
        actions={
          <Button startIcon={<Plus size={16} />} onClick={() => navigate(`${base}/websites/new`)}>
            Add Website
          </Button>
        }
      />

      {isLoading ? (
        <div className={styles.skeletonList}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className={styles.skeletonRow} />
          ))}
        </div>
      ) : domains.length === 0 ? (
        <WaapEmptyCard
          icon={<Globe size={20} />}
          title="No websites yet"
          description="Onboard a website to start accelerating and protecting it with WAAP."
          actions={
            <Button
              startIcon={<Rocket size={16} />}
              onClick={() => navigate(`${base}/onboarding`)}
            >
              Onboarding Domain Now
            </Button>
          }
        />
      ) : (
        <>
          <Toolbar
            start={
              <Input
                placeholder="Filter by domain…"
                startSlot={<Search size={16} />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={styles.filter}
              />
            }
          />

          <div className={styles.tableScroll}>
            <DataTable
              columns={columns}
              data={rows}
              emptyState={
                <EmptyState
                  size="sm"
                  icon={<Globe size={20} />}
                  title="No matching websites"
                  description="Try a different domain filter."
                />
              }
            />
          </div>
        </>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete website?"
        description={`This requests removal of "${pendingDelete?.domain}" (Service ID ${pendingDelete?.service_id}) and its entire CDN/WAAP configuration. Removal is processed asynchronously — it may take a few minutes for the system to remove the website and all its dependencies. This cannot be undone.`}
        confirmLabel="Delete Website"
        destructive
        loading={deleteWebsite.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteWebsite.mutate(pendingDelete.id);
        }}
      />
    </Container>
  );
}
